import { QuestionItemV2, QuestionStatus } from '@examdojo/models/question';
import {
  ChecklistConfig,
  ChecklistItemId,
  ChecklistSection,
  Feedback,
  ImageInfo,
  MarkschemeInfo,
  MCQInfo,
  SectionType,
  TableInfo,
} from '@examdojo/question';
import { Enum, RpcFunction, TableRowModel } from '@examdojo/supabase';
import cloneDeep from 'lodash/cloneDeep';
import { ReviewPermission } from '../permissions/review-permissions.model';

export type ReferenceQuestionStatus = Enum<'reference_question_status'>;

export type MarkschemeDisplayElement = 'all_mark_groups' | 'solution_paths';

export enum DerivativeQuestionReviewType {
  HumanReview = 'needs_human_review',
  PreScreening = 'needs_prescreening',
  ExpertPrescreening = 'needs_expert_prescreening',
  ImageReview = 'needs_image_review',
  ExpertImageReview = 'needs_expert_image_review',
  TieBreak = 'needs_tie_break',
}

export const DerivativeQuestionReviewTypes = Object.values(DerivativeQuestionReviewType);

export enum ReferenceQuestionReviewType {
  Question = 'needs_question_review',
  Markscheme = 'needs_markscheme_review',
  Image = 'needs_image_review',
  ExpertMarkscheme = 'needs_expert_markscheme_review',
  ExpertImage = 'needs_expert_image_review',
}

export function isValidReferenceQuestionReviewType(type: string): type is ReferenceQuestionReviewType {
  return ReferenceQuestionReviewTypes.includes(type as ReferenceQuestionReviewType);
}

export const ReferenceQuestionReviewTypes = Object.values(ReferenceQuestionReviewType);

export type ReviewActionType = Enum<'action_type', 'review'>;

export type ReviewItemHttpModel = Pick<
  TableRowModel<'reviewable_items', 'review'>,
  'question_id' | 'created_at' | 'id' | 'reference_question_id' | 'updated_at'
>;

export type ReviewItemStoreModel = ReviewItemHttpModel;
export type ReviewItemUIModel = ReviewItemHttpModel;

export type CurrentReviewHttpModel = Pick<
  TableRowModel<'current_reviews', 'review'>,
  'reviewable_item_id' | 'user_id' | 'created_at' | 'review_session'
>;

export type ReferenceQuestionFeedbackHttpModel = TableRowModel<'actions', 'review'>;

export const QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING: Record<DerivativeQuestionReviewType, QuestionStatus> = {
  [DerivativeQuestionReviewType.HumanReview]: 'NEEDS_HUMAN_REVIEW',
  [DerivativeQuestionReviewType.PreScreening]: 'NEEDS_PRESCREENING',
  [DerivativeQuestionReviewType.ImageReview]: 'NEEDS_IMAGE_REVIEW',
  [DerivativeQuestionReviewType.TieBreak]: 'NEEDS_TIE_BREAK',
  [DerivativeQuestionReviewType.ExpertPrescreening]: 'NEEDS_EXPERT_PRESCREENING',
  [DerivativeQuestionReviewType.ExpertImageReview]: 'NEEDS_EXPERT_IMAGE_REVIEW',
};

export const QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION: Record<DerivativeQuestionReviewType, ReviewPermission> = {
  [DerivativeQuestionReviewType.HumanReview]: 'derivative_human_review',
  [DerivativeQuestionReviewType.PreScreening]: 'derivative_prescreening',
  [DerivativeQuestionReviewType.ImageReview]: 'derivative_image_review',
  [DerivativeQuestionReviewType.ExpertPrescreening]: 'derivative_expert_prescreening',
  [DerivativeQuestionReviewType.ExpertImageReview]: 'derivative_expert_image_review',
  [DerivativeQuestionReviewType.TieBreak]: 'derivative_tie_break',
};

export const REFERENCE_QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING: Record<
  ReferenceQuestionReviewType,
  ReferenceQuestionStatus
> = {
  [ReferenceQuestionReviewType.Image]: 'NEEDS_IMAGE_REVIEW',
  [ReferenceQuestionReviewType.Question]: 'NEEDS_QUESTION_REVIEW',
  [ReferenceQuestionReviewType.Markscheme]: 'NEEDS_MARKSCHEME_REVIEW',
  [ReferenceQuestionReviewType.ExpertMarkscheme]: 'NEEDS_EXPERT_MARKSCHEME_REVIEW',
  [ReferenceQuestionReviewType.ExpertImage]: 'NEEDS_EXPERT_IMAGE_REVIEW',
};

export const REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_PERMISSION: Record<
  ReferenceQuestionReviewType,
  ReviewPermission
> = {
  [ReferenceQuestionReviewType.Image]: 'reference_image_review',
  [ReferenceQuestionReviewType.Question]: 'reference_question_review',
  [ReferenceQuestionReviewType.Markscheme]: 'reference_markscheme_review',
  [ReferenceQuestionReviewType.ExpertMarkscheme]: 'reference_expert_markscheme_review',
  [ReferenceQuestionReviewType.ExpertImage]: 'reference_expert_image_review',
};

// helper functions to get mode from status using existing mappings
export function getDerivativeModeFromStatus(status: string | null): string | null {
  if (!status) {
    return null;
  }

  // find the mode that maps to this status
  for (const [mode, mappedStatus] of Object.entries(QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING)) {
    if (mappedStatus === status) {
      return mode;
    }
  }
  return null;
}

export function getReferenceModeFromStatus(status: string | null): string | null {
  if (!status) {
    return null;
  }

  // find the mode that maps to this status
  for (const [mode, mappedStatus] of Object.entries(REFERENCE_QUESTION_VALIDATION_STATUS_STRATEGY_MAPPING)) {
    if (mappedStatus === status) {
      return mode;
    }
  }
  return null;
}

export const QUESTION_REVIEW_TYPE_TO_REVIEW_ACTION_TYPE: Record<DerivativeQuestionReviewType, ReviewActionType> = {
  [DerivativeQuestionReviewType.HumanReview]: 'derivative_human_review',
  [DerivativeQuestionReviewType.PreScreening]: 'derivative_prescreening',
  [DerivativeQuestionReviewType.ExpertPrescreening]: 'derivative_expert_prescreening',
  [DerivativeQuestionReviewType.ImageReview]: 'derivative_image_review',
  [DerivativeQuestionReviewType.ExpertImageReview]: 'derivative_expert_image_review',
  [DerivativeQuestionReviewType.TieBreak]: 'derivative_tie_break',
};

export const REFERENCE_QUESTION_REVIEW_TYPE_TO_REVIEW_ACTION_TYPE: Record<
  ReferenceQuestionReviewType,
  ReviewActionType
> = {
  [ReferenceQuestionReviewType.Question]: 'reference_question_review',
  [ReferenceQuestionReviewType.Markscheme]: 'reference_markscheme_review',
  [ReferenceQuestionReviewType.Image]: 'reference_image_review',
  [ReferenceQuestionReviewType.ExpertMarkscheme]: 'reference_expert_markscheme_review',
  [ReferenceQuestionReviewType.ExpertImage]: 'reference_expert_image_review',
};

export const REVIEW_ACTION_TYPE_TO_DERIVATIVE_QUESTION_REVIEW_TYPE: Partial<
  Record<ReviewActionType, DerivativeQuestionReviewType>
> = {
  derivative_human_review: DerivativeQuestionReviewType.HumanReview,
  derivative_prescreening: DerivativeQuestionReviewType.PreScreening,
  derivative_expert_prescreening: DerivativeQuestionReviewType.ExpertPrescreening,
  derivative_image_review: DerivativeQuestionReviewType.ImageReview,
  derivative_expert_image_review: DerivativeQuestionReviewType.ExpertImageReview,
  derivative_tie_break: DerivativeQuestionReviewType.TieBreak,
};

export const REVIEW_ACTION_TYPE_TO_REFERENCE_QUESTION_REVIEW_TYPE: Partial<
  Record<ReviewActionType, ReferenceQuestionReviewType>
> = {
  reference_question_review: ReferenceQuestionReviewType.Question,
  reference_markscheme_review: ReferenceQuestionReviewType.Markscheme,
  reference_image_review: ReferenceQuestionReviewType.Image,
  reference_expert_markscheme_review: ReferenceQuestionReviewType.ExpertMarkscheme,
  reference_expert_image_review: ReferenceQuestionReviewType.ExpertImage,
};

export const REVIEW_PERMISSION_TO_REFERENCE_QUESTION_REVIEW_TYPE: Partial<
  Record<ReviewPermission, ReferenceQuestionReviewType>
> = {
  reference_image_review: ReferenceQuestionReviewType.Image,
  reference_question_review: ReferenceQuestionReviewType.Question,
  reference_markscheme_review: ReferenceQuestionReviewType.Markscheme,
  reference_expert_markscheme_review: ReferenceQuestionReviewType.ExpertMarkscheme,
  reference_expert_image_review: ReferenceQuestionReviewType.ExpertImage,
};

/**
 * Creates a dynamic survey configuration by filtering checklist items based on status
 * and creating separate sections for each markscheme, image, table, and MCQ
 */
export function createDynamicSurveyConfig(
  baseConfig: ChecklistConfig<QuestionStatus>,
  markschemeInfo: MarkschemeInfo[],
  imageInfo: ImageInfo[],
  mcqInfo: MCQInfo[],
  tableInfo: TableInfo[],
  statusStrategyMapping: Record<DerivativeQuestionReviewType, QuestionStatus>,
  mode: DerivativeQuestionReviewType,
): ChecklistConfig<QuestionStatus>;

export function createDynamicSurveyConfig(
  baseConfig: ChecklistConfig<ReferenceQuestionStatus>,
  markschemeInfo: MarkschemeInfo[],
  imageInfo: ImageInfo[],
  mcqInfo: MCQInfo[],
  tableInfo: TableInfo[],
  statusStrategyMapping: Record<ReferenceQuestionReviewType, ReferenceQuestionStatus>,
  mode: ReferenceQuestionReviewType,
): ChecklistConfig<ReferenceQuestionStatus>;

export function createDynamicSurveyConfig<TStatus extends QuestionStatus | ReferenceQuestionStatus>(
  baseConfig: ChecklistConfig<TStatus>,
  markschemeInfo: MarkschemeInfo[],
  imageInfo: ImageInfo[],
  mcqInfo: MCQInfo[],
  tableInfo: TableInfo[],
  statusStrategyMapping: Record<string, TStatus>,
  mode: DerivativeQuestionReviewType | ReferenceQuestionReviewType,
): ChecklistConfig<TStatus> {
  const config = cloneDeep(baseConfig);
  const sections: Array<ChecklistSection<TStatus>> = [];

  // Process sections in their original order to preserve the intended sequence
  // First, add Question sections
  for (const section of config.sections) {
    if (section.section_type === SectionType.Question) {
      const filteredChecklist = section.checklist.filter((item) => {
        if (!item.in_statuses) {
          return true;
        }

        return item.in_statuses.includes(statusStrategyMapping[mode]);
      });
      sections.push({
        ...section,
        section_id: section.section_id ?? '',
        checklist: filteredChecklist,
      });
    }
  }

  // Create separate sections for each markscheme using proper hierarchical naming
  const markschemeSection = config.sections.find((section) => section.section_type === SectionType.Markscheme);
  if (markschemeSection && markschemeInfo.length > 0) {
    const filteredChecklist = markschemeSection.checklist.filter((item) => {
      if (!item.in_statuses) {
        return true;
      }

      return item.in_statuses.includes(statusStrategyMapping[mode]);
    });

    markschemeInfo.forEach((markscheme, index) => {
      sections.push({
        section_id: markscheme.label, // Store the hierarchical name in section_id
        section_type: markschemeSection.section_type,
        section_index: index, // Sequential index starting from 0 for each markscheme
        section_display_name: markschemeSection.section_display_name, // Keep original display name if needed
        checklist: filteredChecklist.map((item) => ({
          ...item,
          id: `${item.id}_markscheme_${index}` as ChecklistItemId,
        })),
        rejection_statuses: markschemeSection.rejection_statuses,
      });
    });
  }

  // Create separate sections for each MCQ
  const mcqSection = config.sections.find((section) => section.section_type === SectionType.MCQ);
  if (mcqSection && mcqInfo.length > 0) {
    const filteredChecklist = mcqSection.checklist.filter((item) => {
      if (!item.in_statuses) {
        return true;
      }

      return item.in_statuses.includes(statusStrategyMapping[mode]);
    });

    mcqInfo.forEach((mcqInfoItem: MCQInfo, index: number) => {
      sections.push({
        section_id: mcqInfoItem.label, // Store the hierarchical name in section_id
        section_type: mcqSection.section_type,
        section_index: index, // Sequential index starting from 0 for each MCQ
        section_display_name: mcqInfoItem.label, // Use the custom label as display name
        checklist: filteredChecklist.map((item) => ({
          ...item,
          id: `${item.id}_mcq_${index}` as ChecklistItemId,
        })),
        rejection_statuses: mcqSection.rejection_statuses,
      });
    });
  }

  // Create separate sections for each image
  const imageSection = config.sections.find((section) => section.section_type === SectionType.Image);
  if (imageSection && imageInfo.length > 0) {
    const filteredChecklist = imageSection.checklist.filter((item) => {
      if (!item.in_statuses) {
        return true;
      }

      return item.in_statuses.includes(statusStrategyMapping[mode]);
    });

    imageInfo.forEach((imageInfoItem: ImageInfo, index: number) => {
      sections.push({
        section_id: imageInfoItem.label, // Store the hierarchical name in section_id
        section_type: imageSection.section_type,
        section_index: index, // Sequential index starting from 0 for each image
        section_display_name: imageInfoItem.label, // Use the hierarchical label for unique display names
        checklist: filteredChecklist.map((item) => ({
          ...item,
          id: `${item.id}_image_${index}` as ChecklistItemId,
          // Store image location context for condition evaluation
          location: imageInfoItem.path.location,
        })),
        rejection_statuses: imageSection.rejection_statuses,
      });
    });
  }

  // Create separate sections for each table
  const tableSection = config.sections.find((section) => section.section_type === SectionType.Table);
  if (tableSection && tableInfo.length > 0) {
    const filteredChecklist = tableSection.checklist.filter((item) => {
      if (!item.in_statuses) {
        return true;
      }

      return item.in_statuses.includes(statusStrategyMapping[mode]);
    });

    tableInfo.forEach((tableInfoItem: TableInfo, index: number) => {
      sections.push({
        section_id: tableInfoItem.label, // Store the hierarchical name in section_id
        section_type: tableSection.section_type,
        section_index: index, // Sequential index starting from 0 for each table
        section_display_name: tableInfoItem.label, // Use the hierarchical label for unique display names
        checklist: filteredChecklist.map((item) => ({
          ...item,
          id: `${item.id}_table_${index}` as ChecklistItemId,
          // Store table location context for condition evaluation
          location: tableInfoItem.path.location,
        })),
        rejection_statuses: tableSection.rejection_statuses,
      });
    });
  }

  // Finally, add Other sections to maintain hierarchy: Question -> Markscheme -> MCQ -> Image -> Table -> Other
  for (const section of config.sections) {
    if (section.section_type === SectionType.Other) {
      const filteredChecklist = section.checklist.filter((item) => {
        if (!item.in_statuses) {
          return true;
        }

        return item.in_statuses.includes(statusStrategyMapping[mode]);
      });
      sections.push({
        ...section,
        section_id: section.section_id ?? '',
        checklist: filteredChecklist,
      });
    }
  }

  return { ...config, sections };
}

export type ReferenceQuestionHttpFeedback = RpcFunction<
  'get_reference_question_feedback_with_snapshot',
  'review'
>['Returns'][number];

export type ReferenceQuestionFeedback = Omit<ReferenceQuestionHttpFeedback, 'question_items' | 'feedback'> & {
  question_items: QuestionItemV2[];
  feedback: Feedback[];
};

export interface DerivativeQuestionFeedback {
  action_id: string;
  feedback: Feedback[];
  action_type: ReviewActionType;
  user_id: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  created_at: string;
  updated_at: string;
  question_items?: QuestionItemV2[];
}

// History entry types
export type ReferenceQuestionHistoryType = 'edit' | 'status_change' | 'review_feedback';

export interface BaseReferenceQuestionHistoryEntry {
  id: string;
  type: ReferenceQuestionHistoryType;
  timestamp?: string | null;
  user_id: string | null;
  question_items?: QuestionItemV2[];
}

export interface ReferenceQuestionEditHistoryEntry extends BaseReferenceQuestionHistoryEntry {
  type: 'edit';
}

export interface ReferenceQuestionStatusChangeHistoryEntry extends BaseReferenceQuestionHistoryEntry {
  type: 'status_change';
  status: string | null;
  status_reason?: string | null;
}

export interface ReferenceQuestionReviewHistoryEntry extends BaseReferenceQuestionHistoryEntry {
  type: 'review_feedback';
  action_type: ReviewActionType;
  feedback: Feedback[] | null;
}

export type ReferenceQuestionHistoryEntry =
  | ReferenceQuestionEditHistoryEntry
  | ReferenceQuestionStatusChangeHistoryEntry
  | ReferenceQuestionReviewHistoryEntry;

export enum QuestionType {
  Derivative = 'derivative',
  Reference = 'reference',
}
